import {Typography} from '@yandex-delivery/blocks'
import type {VerticalTimelineItem as VerticalTimelineItemType} from '@yandex-delivery/types/schemas/services/cargo-c2c/definitions/timeline'
import clsx from 'clsx'
import React, {useEffect, useRef, useState} from 'react'
import {tagToUrl} from 'src/shared/utils'

import {classes} from './VerticalTimelineItem.classes'

type Props = {
  item: VerticalTimelineItemType
  isFirst: boolean
  isLast: boolean
  isLastActive: boolean
  isLastActiveWithIcon: boolean
  index: number
  lastActiveIndex: number
}

function getColorFromModel(color: VerticalTimelineItemType['title_color'], rgbVariable: string): string | undefined {
  if (!color?.alpha_day) return undefined
  const {alpha_day} = color
  return `rgba(var(${rgbVariable}), ${alpha_day})`
}

export const VerticalTimelineItem: React.FC<Props> = ({
  item,
  isFirst,
  isLast,
  isLastActive,
  isLastActiveWithIcon,
  index,
  lastActiveIndex,
}) => {
  const rowRef = useRef<HTMLDivElement>(null)
  const [rowHeight, setRowHeight] = useState(56)

  useEffect(() => {
    if (rowRef.current) {
      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          setRowHeight(entry.contentRect.height)
        }
      })
      observer.observe(rowRef.current)
      return () => observer.disconnect()
    }
  }, [])

  const isActive = item.status === 'passed'
  const connectorColor = isActive ? 'var(--ui-colors-control-main)' : 'var(--ui-colors-background-minor)'
  const titleColor =
    getColorFromModel(item.title_color, '--ui-colors-text-rgb') ??
    (isActive ? 'var(--ui-colors-text)' : 'var(--ui-colors-text-minor)')
  const subtitleColor = getColorFromModel(item.subtitle_color, '--ui-colors-text-rgb') ?? 'var(--ui-colors-text-minor)'

  return (
    <div className={classes.row} ref={rowRef}>
      {/* Левая колонка: дата и время */}
      <div className={classes.leadColumn}>
        {item.lead_title && (
          <Typography className={classes.leadTitle} color={isActive ? 'main' : 'minor'} variant="caption1">
            {item.lead_title}
          </Typography>
        )}
        {item.lead_subtitle && (
          <Typography className={classes.leadSubtitle} color="minor" variant="caption1">
            {item.lead_subtitle}
          </Typography>
        )}
      </div>

      {/* Центральная колонка: коннекторы и иконка (аналогично Android VerticalTimelineScreen) */}
      <div className={classes.iconColumn} style={{minHeight: `${Math.max(rowHeight, 56)}px`}}>
        {/* Коннекторы - пропускаем только для последнего активного С ИКОНКОЙ (с spotlight) */}
        {!(item.icon_tag && isLastActiveWithIcon) && (
          <>
            {/* Верхний коннектор - от центра к верху, высота = rowHeight / 2 */}
            {!isFirst && (
              <div
                className={classes.connectorTop}
                style={{
                  height: `${rowHeight / 2}px`,
                  backgroundColor: connectorColor,
                  borderRadius: index === lastActiveIndex + 1 ? '15px' : '0',
                }}
              />
            )}

            {/* Нижний коннектор - от центра к низу, высота = rowHeight / 2 */}
            {!isLast && (
              <div
                className={classes.connectorBottom}
                style={{
                  height: `${rowHeight / 2}px`,
                  backgroundColor: connectorColor,
                  borderRadius: index === lastActiveIndex - 1 ? '15px' : '0',
                }}
              />
            )}
          </>
        )}

        {/* Иконка/индикатор */}
        <div className={classes.iconWrapper}>
          {/* Spotlight для последнего активного с иконкой */}
          {item.icon_tag && isLastActiveWithIcon && (
            <div
              className={classes.iconSpotlight}
              style={{backgroundColor: 'rgba(var(--ui-colors-control-main-rgb), 0.2)'}}
            />
          )}

          {/* Основной круг */}
          <div
            className={clsx(classes.iconSpot, !item.icon_tag && classes.iconSpotSmall)}
            style={{backgroundColor: connectorColor}}
          >
            {item.icon_tag && (
              <img
                alt=""
                className={classes.icon}
                {...(!isActive && {'data-theme-invert': true})}
                loading="lazy"
                src={tagToUrl(item.icon_tag)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Правая колонка: заголовок и подзаголовок */}
      <div className={classes.trailColumn}>
        <Typography
          className={clsx(classes.title, item.subtitle && classes.titleWithSubtitle)}
          style={{color: titleColor}}
          variant="body2"
        >
          {item.title}
        </Typography>
        {item.subtitle && (
          <Typography className={classes.subtitle} style={{color: subtitleColor}} variant="caption1">
            {item.subtitle}
          </Typography>
        )}
      </div>
    </div>
  )
}
